import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Alert,
  Button,
  TextField,
  Box,
  Dialog,
  DialogContent,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentConfirm from './PaymentConfirm';
import TopupForm from './method/TopupForm';
import api from '../../apis/api';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import AddCardIcon from '@mui/icons-material/AddCard';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PaymentForm = ({ user, primaryPaymentMethod }) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [credits, setCredits] = useState(0);
  const [amountValue, setAmountValue] = useState(0);
  const [creditsError, setCreditsError] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState({});
  const [topup, setTopup] = useState(false);
  const [pristine, setPristine] = useState(true);

  const onSubmit = async (value) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/billing/payment`, {
        amount: Math.round(parseFloat(value) * 100),
        currency: 'usd',
      });
      setConfirming(true);
      setPaymentIntent(res.data);
      setSubmitting(false);
    } catch (error) {
      if (error.response) {
        setSubmitting(false);
        // need to display this error to the user somehow
        console.log(error.response.data.errorMessage);
      }
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setAmountValue(value);
    setCredits(value);
    setPristine(false);
    setCreditsError(isNaN(value));
  };

  return (
    <>
      <Dialog
        open={topup}
        onClose={() => setTopup(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          <TopupForm
            user={user}
            primaryPaymentMethod={primaryPaymentMethod}
            onFinish={() => setTopup(false)}
          />
        </DialogContent>
      </Dialog>

      <Paper sx={{ p: 3 }}>
        <Typography variant='h4' gutterBottom>
          Purchase Credits
        </Typography>
        <Alert severity='info' sx={{ mb: 3 }}>
          <Typography variant='subtitle1' gutterBottom>
            Credits can be purchased for $1.00 per credit
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>There is a minimum transaction amount of 25 credits ($25)</li>
            <li>
              If you have multiple payment methods, the Primary payment method
              will be used.
            </li>
          </ul>
        </Alert>

        <Box component='form' sx={{ mb: 3 }}>
          <TextField
            fullWidth
            name='credits'
            label='Amount of Credits'
            value={credits}
            required
            onChange={handleChange}
            error={creditsError}
            helperText={
              creditsError ? 'Please enter a valid amount (>= 25)' : undefined
            }
            sx={{ mb: 2 }}
          />
          {!pristine && (
            <Typography variant='body1' sx={{ mb: 2 }}>
              Charge {formatter.format(credits)}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <LoadingButton
              variant='contained'
              color='brand'
              onClick={() => onSubmit(amountValue)}
              disabled={pristine || creditsError || credits < 25}
              startIcon={<ShoppingCartIcon />}
              loading={submitting}
            >
              Buy
            </LoadingButton>
            <Button
              component={Link}
              to='/account/billing/history'
              variant='outlined'
              startIcon={<HistoryIcon />}
            >
              View History
            </Button>
            <Button
              variant='outlined'
              onClick={() => setTopup(true)}
              startIcon={<AddCardIcon />}
            >
              Configure
            </Button>
          </Box>
        </Box>

        <Elements
          stripe={loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)}
        >
          <PaymentConfirm
            amount={amountValue}
            credits={credits}
            onCancel={() => setConfirming(false)}
            open={confirming}
            paymentIntent={paymentIntent}
          />
        </Elements>
      </Paper>
    </>
  );
};

export default PaymentForm;
